import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: false
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/auth')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Export raw api instance for direct usage (e.g., blob downloads)
export { api };

// Auth APIs
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout')
};

// Tutor APIs
export const tutorAPI = {
  list: (params) => api.get('/tutors', { params }),
  get: (id) => api.get(`/tutors/${id}`),
  getMyProfile: () => api.get('/tutors/me/profile'),
  updateProfile: (data) => api.put('/tutors/profile', data),
  getAvailability: (id, date) => api.get(`/tutors/${id}/availability`, { params: { date } }),
  matchBySubjects: (subjects, gradeLevel) => {
    const params = new URLSearchParams();
    subjects.forEach(s => params.append('subjects', s));
    if (gradeLevel) params.append('grade_level', gradeLevel);
    return api.post(`/tutors/match?${params.toString()}`);
  }
};

// Booking APIs
export const bookingAPI = {
  create: (data) => api.post('/bookings', data),
  list: (params) => api.get('/bookings', { params }),
  get: (id) => api.get(`/bookings/${id}`),
  update: (id, data) => api.put(`/bookings/${id}`, data),
  cancel: (id) => api.delete(`/bookings/${id}`)
};

// Payment APIs
export const paymentAPI = {
  createCheckout: (data) => api.post('/payments/create-checkout', data),
  createPackageCheckout: (data) => api.post('/payments/create-package-checkout', data),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getPackageStatus: (sessionId) => api.get(`/payments/package-status/${sessionId}`)
};

// Review APIs
export const reviewAPI = {
  create: (data) => api.post('/reviews', data),
  list: (tutorId, params) => api.get(`/reviews/${tutorId}`, { params })
};

// Message APIs
export const messageAPI = {
  send: (data) => api.post('/messages', data),
  listConversations: () => api.get('/messages'),
  getConversation: (partnerId) => api.get(`/messages/${partnerId}`)
};

// Zoom APIs
export const zoomAPI = {
  createMeeting: (data) => api.post('/zoom/create-meeting', data)
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getBusinessMetrics: () => api.get('/admin/business-metrics'),
  getPendingTutors: () => api.get('/admin/pending-tutors'),
  approveTutor: (userId) => api.post(`/admin/approve-tutor/${userId}`),
  listUsers: (params) => api.get('/admin/users', { params }),
  listBookings: (params) => api.get('/admin/bookings', { params }),
  getStudentsConsent: (params) => api.get('/admin/students-consent', { params }),
  // User Archive Management
  archiveUser: (userId) => api.post(`/admin/users/${userId}/archive`),
  unarchiveUser: (userId) => api.post(`/admin/users/${userId}/unarchive`),
  // Payment management
  listPayments: (params) => api.get('/admin/payments', { params }),
  getPaymentDetails: (paymentId) => api.get(`/admin/payments/${paymentId}`),
  // Order management (e-transfer)
  listOrders: (params) => api.get('/admin/orders', { params }),
  updateOrder: (orderId, data) => api.put(`/admin/orders/${orderId}`, data),
  // Chat management - now with conversations
  getChatMessages: (params) => api.get('/admin/chat/messages', { params }),
  getConversation: (conversationId) => api.get(`/admin/chat/conversation/${conversationId}`),
  replyChatMessage: (data) => api.post('/admin/chat/reply', data),
  markChatRead: (messageId) => api.put(`/admin/chat/mark-read/${messageId}`),
  markConversationRead: (conversationId) => api.put(`/admin/chat/mark-conversation-read/${conversationId}`),
  markAllChatRead: () => api.put('/admin/chat/mark-all-read'),
  // Tutor management
  listTutors: (params) => api.get('/admin/tutors', { params }),
  createTutor: (data) => api.post('/admin/tutors', data),
  updateTutor: (profileId, data) => api.put(`/admin/tutors/${profileId}`, data),
  deleteTutor: (profileId) => api.delete(`/admin/tutors/${profileId}`),
  approveTutorProfile: (profileId) => api.put(`/admin/tutors/${profileId}/approve`),
  unapproveTutorProfile: (profileId) => api.put(`/admin/tutors/${profileId}/unapprove`),
  toggleTutorVisibility: (profileId) => api.put(`/admin/tutors/${profileId}/toggle-visibility`),
  // Tutor Assignment
  getApprovedTutors: () => api.get('/admin/tutors/approved'),
  assignTutor: (studentId, tutorId) => api.post(`/admin/students/${studentId}/assign-tutor`, { tutor_id: tutorId }),
  unassignTutor: (studentId) => api.delete(`/admin/students/${studentId}/unassign-tutor`),
  // Tutor Approved Rates
  getTutorApprovedRates: (tutorId) => api.get(`/admin/tutor/${tutorId}/approved-rates`),
  updateTutorApprovedRates: (tutorId, data) => api.put(`/admin/tutor/${tutorId}/approved-rates`, data),
  // Session Management
  listSessions: (params) => api.get('/admin/sessions', { params }),
  approveSession: (logId, data) => api.put(`/admin/sessions/${logId}/approve`, data),
  getTutorHoursOverview: () => api.get('/admin/tutor-hours-overview'),
  // Tutor Payroll
  getTutorPayroll: (params) => api.get('/admin/tutor-payroll', { params }),
  createTutorPayment: (data) => api.post('/admin/tutor-payments', data),
  getTutorPaymentHistory: (params) => api.get('/admin/tutor-payments/history', { params }),
  reverseTutorPayment: (paymentId) => api.delete(`/admin/tutor-payments/${paymentId}`),
  backfillPayrollFields: () => api.post('/admin/tutor-payroll/backfill'),
  getSessionPayrollDetails: (logId) => api.get(`/admin/session-logs/${logId}/payroll`),
  updateSessionPayroll: (logId, data) => api.put(`/admin/session-logs/${logId}/payroll`, data),
  // Tutor Invoices
  getTutorInvoices: (params) => api.get('/admin/invoices/list', { params }),
  getInvoiceById: (invoiceId) => api.get(`/admin/invoices/${invoiceId}`),
  generateInvoice: (data) => api.post('/admin/invoices/generate', data),
  regenerateInvoice: (data) => api.post('/admin/invoices/regenerate', data),
  previewInvoice: (data) => api.post('/admin/invoices/preview', data),
  getInvoiceEligibleTutors: (data) => api.post('/admin/invoices/eligible-tutors', data),
  sendInvoiceEmail: (invoiceId, recipientType) => api.post(`/admin/invoices/${invoiceId}/send-email`, { recipient_type: recipientType }),
  checkEmailStatus: () => api.get('/admin/invoices/email/status'),
  getInvoicePeriods: () => api.get('/admin/invoices/periods/current'),
  getPreviousInvoicePeriod: () => api.get('/admin/invoices/periods/previous'),
  runInvoiceBatch: (data) => api.post('/admin/invoices/scheduler/run', data),
  getSchedulerRuns: (limit = 10) => api.get('/admin/invoices/scheduler/runs', { params: { limit } }),
  simulateInvoice15th: (force = false) => api.post('/admin/invoices/scheduler/simulate-15th', null, { params: { force } }),
  simulateInvoiceMonthEnd: (force = false) => api.post('/admin/invoices/scheduler/simulate-month-end', null, { params: { force } }),
  // Student Hours Management
  generateTutorLink: (userId) => api.post(`/admin/students/${userId}/tutor-link`),
  regenerateTutorLink: (userId) => api.post(`/admin/students/${userId}/tutor-link/regenerate`),
  toggleTutorLink: (userId) => api.put(`/admin/students/${userId}/tutor-link/toggle`),
  getStudentHours: (userId) => api.get(`/admin/students/${userId}/hours`),
  updateStudentHours: (userId, data) => api.put(`/admin/students/${userId}/hours`, data),
  editSessionLog: (logId, data) => api.put(`/admin/session-logs/${logId}`, data),
  deleteSessionLog: (logId) => api.delete(`/admin/session-logs/${logId}`),
  backfillHours: () => api.post('/admin/backfill-hours'),
  // Tutor Review APIs
  getTutorFullReview: (tutorId) => api.get(`/admin/tutors/${tutorId}/full-review`),
  getTutorAgreement: (tutorId) => api.get(`/admin/tutors/${tutorId}/agreement`),
  getTutorDocuments: (tutorId) => api.get(`/admin/tutors/${tutorId}/documents`),
  reviewTutorDocument: (tutorId, documentId, data) => api.put(`/admin/tutors/${tutorId}/documents/${documentId}/review`, data),
  updateTutorStatus: (tutorId, data) => api.put(`/admin/tutors/${tutorId}/status`, data),
  addTutorNote: (tutorId, data) => api.post(`/admin/tutors/${tutorId}/notes`, data),
  deleteTutorNote: (tutorId, noteId) => api.delete(`/admin/tutors/${tutorId}/notes/${noteId}`)
};

// Orders API (for customers)
export const ordersAPI = {
  createEtransfer: (data) => api.post('/orders/create-etransfer', data),
  confirmSent: (orderId) => api.post(`/orders/${orderId}/confirm-sent`),
  getMyOrders: () => api.get('/orders/my-orders')
};

// Student Hours API
export const studentHoursAPI = {
  getMyHours: () => api.get('/student/hours')
};

// Tutor Submission API (no auth)
export const tutorSubmissionAPI = {
  submit: (data) => api.post('/tutor-submission', data)
};

// File Upload API
export const uploadAPI = {
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadFile: (formData) => {
    return api.post('/upload/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Live Chat APIs
export const chatAPI = {
  sendMessage: (data) => api.post('/chat/messages', data),
  getMessages: (params) => api.get('/chat/messages', { params }),
  markAsRead: (guestId) => api.put('/chat/mark-read', null, { params: { guest_id: guestId } })
};

// Helper to get or create guest_id for anonymous users
export const getGuestId = () => {
  let guestId = localStorage.getItem('chat_guest_id');
  if (!guestId) {
    guestId = `guest_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('chat_guest_id', guestId);
  }
  return guestId;
};

// Student Profile APIs
export const studentAPI = {
  getProfile: () => api.get('/student/profile'),
  updateProfile: (data) => api.put('/student/profile', data),
  getBookingEligibility: () => api.get('/student/booking-eligibility')
};

// Consent APIs
export const consentAPI = {
  getRequest: (token) => api.get(`/consent/${token}`),
  processConsent: (token, action) => api.post(`/consent/${token}`, { consent_token: token, action }),
  getStatus: (userId) => api.get(`/consent/status/${userId}`)
};

// Materials APIs
export const materialsAPI = {
  upload: (data) => api.post('/materials', data),
  list: (params) => api.get('/materials', { params })
};

// Subjects API
export const subjectsAPI = {
  list: () => api.get('/subjects')
};

// Tutoring Requests API
export const tutoringRequestAPI = {
  create: (data) => api.post('/tutoring-requests', data),
  list: (status) => api.get('/tutoring-requests', { params: { status } }),
  adminList: (params) => api.get('/admin/tutoring-requests', { params }),
  adminUpdate: (requestId, data) => api.put(`/admin/tutoring-requests/${requestId}`, data)
};

// Admin Notifications API
export const notificationsAPI = {
  list: (unreadOnly = false, limit = 50) => api.get('/admin/notifications', { params: { unread_only: unreadOnly, limit } }),
  markRead: (notificationId) => api.put(`/admin/notifications/${notificationId}/read`),
  markAllRead: () => api.put('/admin/notifications/read-all')
};

// Team Members API
export const teamAPI = {
  list: () => api.get('/team-members'),
  adminList: () => api.get('/admin/team-members'),
  create: (data) => api.post('/admin/team-members', data),
  update: (memberId, data) => api.put(`/admin/team-members/${memberId}`, data),
  delete: (memberId) => api.delete(`/admin/team-members/${memberId}`),
  reorder: (order) => api.post('/admin/team-members/reorder', { order }),
  uploadImage: (memberId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/team-members/${memberId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// CMS Content Management APIs
export const cmsAPI = {
  // Testimonials
  listTestimonials: () => api.get('/admin/content/testimonials'),
  createTestimonial: (data) => api.post('/admin/content/testimonials', data),
  updateTestimonial: (id, data) => api.put(`/admin/content/testimonials/${id}`, data),
  deleteTestimonial: (id) => api.delete(`/admin/content/testimonials/${id}`),
  seedTestimonials: () => api.post('/admin/content/testimonials/seed'),
  // FAQs
  listFaqs: () => api.get('/admin/content/faqs'),
  createFaq: (data) => api.post('/admin/content/faqs', data),
  updateFaq: (id, data) => api.put(`/admin/content/faqs/${id}`, data),
  deleteFaq: (id) => api.delete(`/admin/content/faqs/${id}`),
  // Policies
  listPolicies: () => api.get('/admin/content/policies'),
  createPolicy: (data) => api.post('/admin/content/policies', data),
  updatePolicy: (id, data) => api.put(`/admin/content/policies/${id}`, data),
  deletePolicy: (id) => api.delete(`/admin/content/policies/${id}`),
  // Pricing Packages
  listPricingPackages: () => api.get('/admin/content/pricing-packages'),
  createPricingPackage: (data) => api.post('/admin/content/pricing-packages', data),
  updatePricingPackage: (id, data) => api.put(`/admin/content/pricing-packages/${id}`, data),
  deletePricingPackage: (id) => api.delete(`/admin/content/pricing-packages/${id}`),
  // Pricing Tiers (Calculator Config)
  listPricingTiers: () => api.get('/admin/content/pricing-tiers'),
  createPricingTier: (data) => api.post('/admin/content/pricing-tiers', data),
  updatePricingTier: (id, data) => api.put(`/admin/content/pricing-tiers/${id}`, data),
  deletePricingTier: (id) => api.delete(`/admin/content/pricing-tiers/${id}`)
};

// Public Content APIs (no auth required)
export const publicContentAPI = {
  getTestimonials: () => api.get('/public/testimonials'),
  getFaqs: () => api.get('/public/faqs'),
  getPolicies: () => api.get('/public/policies'),
  getPricingPackages: () => api.get('/public/pricing-packages'),
  getPricingTiers: () => api.get('/public/pricing-tiers')
};

export default api;
